import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * A modal dialog.
 *
 * ── WHY THIS IS HAND-WRITTEN ─────────────────────────────────────────────────
 *
 * `ui-ux.md` says to prefer a shadcn primitive for a Dialog, and that is good advice — the focus
 * details are genuinely fiddly. But shadcn's CLI was never set up in this repository: there is no
 * `components.json` anywhere, and every existing component (Button, StatusPill, States) is
 * hand-written with `cva` and the design tokens. Introducing the CLI for one component would leave
 * two conventions in the codebase, which `coding-standards.md` is explicit is worse than either
 * applied consistently.
 *
 * So the accessibility work is done here explicitly, and the four things that are easy to get wrong
 * are each commented:
 *
 *   1. Focus MOVES IN on open and RETURNS to the trigger on close.
 *   2. Focus is TRAPPED — Tab from the last element wraps to the first, not out to the page behind.
 *   3. Escape closes.
 *   4. It sits on `--raised`, a step ABOVE the page. In dark mode a dialog at the same ink as the
 *      page stops reading as modal, which is the single most common dark-mode dialog failure.
 */
export function Modal({
  title,
  description,
  onClose,
  isLocked,
  children,
  footer,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  /**
   * Removes every way out — the close button, Escape and the backdrop click — while an operation is
   * in flight.
   *
   * Added for the generation dialog, where closing mid-flight neither cancels the work nor refunds
   * it, and the design would land in a campaign nobody was looking at. The alternative that was
   * tried first — passing a no-op `onClose` — left a close button that looked live and did nothing,
   * which is worse than one that is visibly unavailable.
   *
   * Use sparingly. A dialog somebody cannot leave is hostile unless the reason is real and visible,
   * so whatever sets this must also be saying on screen what it is waiting for.
   */
  isLocked?: boolean | undefined;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Remembered before focus moves, so it can be restored on close. Without this, closing a dialog
    // dumps keyboard focus at the top of the document and a keyboard user loses their place entirely.
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const focusable = panel.current?.querySelectorAll<HTMLElement>(FocusableSelector);

    focusable?.[0]?.focus();

    return () => previouslyFocused.current?.focus();
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();

        if (isLocked !== true)
          onClose();

        return;
      }

      if (event.key !== 'Tab') return;

      // The trap. Without it, Tab walks out of the dialog into the page behind — which is still
      // there, still interactive, and invisible to a keyboard user who cannot see where they are.
      const focusable = [...(panel.current?.querySelectorAll<HTMLElement>(FocusableSelector) ?? [])];

      const first = focusable.at(0);
      const last = focusable.at(-1);

      if (first === undefined || last === undefined) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, isLocked]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      // The backdrop closes on click, but only when the click started on the backdrop itself —
      // otherwise a drag that ends outside the panel closes a dialog somebody was typing in.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && isLocked !== true) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        {...(description === undefined ? {} : { 'aria-describedby': 'modal-description' })}
        className={cn(
          // `--raised`, a step above the page. In dark mode a dialog on `--surface` disappears into
          // the background and stops reading as modal at all.
          'w-full max-w-lg overflow-hidden rounded-t-xl bg-raised shadow-2xl sm:rounded-xl',
          // A flex COLUMN with only the body scrolling, rather than `overflow-y-auto` on the whole
          // panel. With the panel scrolling, a dialog taller than 90vh scrolled its own title and
          // Close button out of view — so the longest dialogs, the ones most in need of a way out,
          // were the ones that hid it.
          'flex max-h-[90vh] flex-col border border-border',
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div>
            <h2 id="modal-title" className="text-base font-semibold text-heading">
              {title}
            </h2>

            {description !== undefined && (
              <p id="modal-description" className="mt-1 text-sm text-muted">
                {description}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isLocked === true}
            // An icon-only button needs an accessible name, and 44px minimum for touch.
            aria-label="Close"
            className="-mr-1 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted hover:bg-chip disabled:pointer-events-none disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer !== undefined && (
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border bg-surface-2 px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/** Everything focusable, excluding anything deliberately removed from the tab order. */
const FocusableSelector =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
